/**
 * EmailClientPage — Jul-2026 (User-Wunsch): integriertes E-Mail-Programm
 * („Outlook im System"). Konten (IMAP-Anbindung des eigenen Postfachs ODER
 * System-Adresse), Posteingang/Gesendet, Antworten mit KI-Vorschlägen,
 * Textvorlagen, Wiedervorlage/Erinnerungen, Gelesen/Erledigt.
 * Rechte: Filialleiter/HQ sehen zusätzlich die Postfächer ihrer Mitarbeiter.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Mail, Plus, RefreshCw, Loader2, Send, Reply, Forward, Sparkles, Clock, CheckCircle2,
    Trash2, Inbox, SendHorizonal, BellRing, X, FileText, User, Paperclip,
    Tag, UserPlus, Settings2, FileUp, PenLine, CheckCheck, Download, Eye, Zap, Pin, Bike, AlertTriangle, PlugZap,
} from "lucide-react";
import { Archive } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useGearsVisible } from "../lib/gearVisibility";
import { useAuth } from "../lib/auth";
import InfoHint from "../components/listing/InfoHint";

const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
};

const fmtDur = (min) => {
    if (min == null) return "";
    if (min < 1) return "sofort";
    if (min < 60) return `${min} Min`;
    if (min < 1440) return `${Math.round(min / 6) / 10} Std`.replace(".", ",");
    return `${Math.round(min / 1440)} Tg`;
};

const CATEGORIES = ["Anfrage", "Verkauf", "Werkstatt", "Rechnung", "Reklamation", "Termin", "Sonstiges"];

// Aug-2026 (User-Wunsch): URLs in Text-Mails anklickbar machen (öffnen in neuem Tab).
const linkifyText = (text) => {
    const parts = String(text || "").split(/(https?:\/\/[^\s<>"']+)/g);
    return parts.map((p, i) => /^https?:\/\//.test(p)
        ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline break-all" data-testid="email-text-link">{p}</a>
        : p);
};

/* ---------------------------- Konto verbinden ---------------------------- */
function ConnectAccountDialog({ onClose, onCreated }) {
    const [type, setType] = useState("imap");
    const [f, setF] = useState({ label: "", email_address: "", receive_mode: "imap", imap_host: "", imap_port: "993", imap_user: "", imap_password: "", smtp_host: "", smtp_port: "587", forward_to: "", signature: "", use_standard_signature: true });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    // Jul-2026 (User-Report) — Microsoft-365-Adressen automatisch vorbelegen:
    // M365 sperrt IMAP-Passwort-Login → Modus „Nur Senden" + Serverdaten setzen.
    const applyEmail = (v) => {
        setF((p) => {
            const next = { ...p, email_address: v };
            const mail = v.trim().toLowerCase();
            if (/@(autohaus-koenig\.de|outlook\.|hotmail\.|live\.)/.test(mail) || p.imap_host === "outlook.office365.com") {
                next.imap_host = "outlook.office365.com";
                next.smtp_host = "smtp.office365.com";
                next.smtp_port = "587";
                next.imap_user = mail;
                next.receive_mode = "smtp_only";
            }
            return next;
        });
    };

    const save = async () => {
        setBusy(true);
        try {
            const r = await api.post("/email/accounts", { type, ...f });
            toast.success(type === "imap" ? "Postfach verbunden" : `System-Adresse angelegt: ${r.data.account.email_address}`);
            onCreated();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Verbindung fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="email-connect-dialog">
                <DialogHeader><DialogTitle>Postfach verbinden</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => setType("imap")} className={`border rounded-sm p-3 text-left text-xs ${type === "imap" ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200"}`} data-testid="email-type-imap">
                            <div className="font-black text-sm">Eigenes Postfach (IMAP)</div>
                            Bestehende Adresse (z. B. IONOS, Strato, Outlook 365, Gmail) verbinden — Empfang & Versand über die eigene Adresse.
                        </button>
                        <button type="button" onClick={() => setType("system")} className={`border rounded-sm p-3 text-left text-xs ${type === "system" ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200"}`} data-testid="email-type-system">
                            <div className="font-black text-sm">System-Adresse</div>
                            Neue Adresse @mail.twowheels.info — Versand sofort, Empfang startet mit Resend-Freischaltung.
                        </button>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-600">Bezeichnung</label>
                        <Input value={f.label} onChange={(e) => set("label", e.target.value)} placeholder="z. B. Verkauf Max" className="rounded-sm" data-testid="email-acc-label" />
                    </div>
                    {type === "imap" && (
                        <>
                            <div>
                                <label className="text-xs font-bold text-gray-600">E-Mail-Adresse</label>
                                <Input value={f.email_address} onChange={(e) => applyEmail(e.target.value)} placeholder="info@haendler.de" className="rounded-sm" data-testid="email-acc-address" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-600">Empfang</label>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    <button type="button" onClick={() => set("receive_mode", "imap")} className={`border rounded-sm p-2 text-left text-[11px] ${f.receive_mode === "imap" ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200"}`} data-testid="email-mode-imap">
                                        <div className="font-black">Empfang + Versand (IMAP)</div>
                                        Posteingang wird alle 5 Min. abgerufen.
                                    </button>
                                    <button type="button" onClick={() => set("receive_mode", "smtp_only")} className={`border rounded-sm p-2 text-left text-[11px] ${f.receive_mode === "smtp_only" ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200"}`} data-testid="email-mode-smtp-only">
                                        <div className="font-black">Nur Senden (SMTP)</div>
                                        Für Microsoft 365/Outlook: IMAP-Passwort-Login ist dort von Microsoft gesperrt — Versand funktioniert.
                                    </button>
                                </div>
                            </div>
                            {f.receive_mode === "imap" && (
                            <div className="grid grid-cols-3 gap-2">
                                <div className="col-span-2"><label className="text-xs font-bold text-gray-600">IMAP-Server</label><Input value={f.imap_host} onChange={(e) => set("imap_host", e.target.value)} placeholder="imap.ionos.de" className="rounded-sm" data-testid="email-acc-imap-host" /></div>
                                <div><label className="text-xs font-bold text-gray-600">Port</label><Input value={f.imap_port} onChange={(e) => set("imap_port", e.target.value)} className="rounded-sm" /></div>
                            </div>
                            )}
                            <div className="grid grid-cols-2 gap-2">
                                <div><label className="text-xs font-bold text-gray-600">Benutzer</label><Input value={f.imap_user} onChange={(e) => set("imap_user", e.target.value)} placeholder="meist die E-Mail-Adresse" className="rounded-sm" data-testid="email-acc-imap-user" /></div>
                                <div><label className="text-xs font-bold text-gray-600">Passwort</label><Input type="password" value={f.imap_password} onChange={(e) => set("imap_password", e.target.value)} className="rounded-sm" data-testid="email-acc-imap-pass" /></div>
                            </div>
                            {f.receive_mode === "smtp_only" && (
                                <p className="text-[11px] text-gray-500 bg-blue-50 border-l-4 border-blue-400 px-2 py-1.5" data-testid="email-smtp-only-hint">
                                    Nur-Senden-Modus: Es wird nur die SMTP-Anmeldung getestet. Für den Empfang können Sie eingehende Mails per Regel an Ihre TwoWheels-System-Adresse weiterleiten.
                                </p>
                            )}
                            <div className="grid grid-cols-3 gap-2">
                                <div className="col-span-2"><label className="text-xs font-bold text-gray-600">SMTP-Server (Versand)</label><Input value={f.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.ionos.de" className="rounded-sm" data-testid="email-acc-smtp-host" /></div>
                                <div><label className="text-xs font-bold text-gray-600">Port</label><Input value={f.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} className="rounded-sm" /></div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-600">Automatische Weiterleitung an (optional)</label>
                                <Input type="email" value={f.forward_to} onChange={(e) => set("forward_to", e.target.value)} placeholder="z. B. scholz.home1@gmail.com" className="rounded-sm" data-testid="email-acc-forward-to" />
                                <p className="text-[11px] text-gray-500 mt-0.5">Jede neu eingehende Mail wird beim Abruf automatisch als Kopie an diese Adresse weitergeleitet.</p>
                            </div>
                        </>
                    )}
                    <label className="flex items-start gap-2 cursor-pointer bg-[#FFFBEB] border border-[#FFCC00]/50 rounded-sm p-2.5" data-testid="email-acc-standard-toggle">
                        <input type="checkbox" checked={f.use_standard_signature} onChange={(e) => set("use_standard_signature", e.target.checked)} className="w-4 h-4 mt-0.5" data-testid="email-acc-standard-cb" />
                        <span className="text-sm">
                            <span className="font-bold">Standard-Signatur verwenden</span>
                            <span className="block text-[11px] text-gray-600">Automatisch aus Standort + eingeloggtem Mitarbeiter — keine manuelle Pflege nötig.</span>
                        </span>
                    </label>
                    {!f.use_standard_signature && (
                    <div>
                        <label className="text-xs font-bold text-gray-600">Signatur (optional, wird unter jede Mail gesetzt)</label>
                        <Textarea value={f.signature} onChange={(e) => set("signature", e.target.value)} rows={2} className="rounded-sm" data-testid="email-acc-signature" />
                    </div>
                    )}
                    <Button onClick={save} disabled={busy} className="w-full bg-[#111827] text-white rounded-sm" data-testid="email-acc-save">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} {type === "imap" ? "Verbinden & testen" : "System-Adresse anlegen"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ------------------------------- Composer -------------------------------- */
// Jun-2026 (User-Wunsch) — beim Antworten auf Börsen-/System-Mails (noreply)
// die echte Kunden-E-Mail aus dem Mail-INHALT als Empfänger vorschlagen.
const NOREPLY_RE = /(noreply|no-reply|donotreply|notification|newsletter|mailer-daemon)@|@(team\.mobile\.de|mail\.kleinanzeigen\.de|ebay|autoscout24)/i;
function bestReplyAddress(replyTo, ownAddress) {
    const from = (replyTo?.from_addr || "").trim();
    if (!from || !NOREPLY_RE.test(from)) return from;
    const text = `${replyTo?.body_text || ""}\n${replyTo?.body_html || ""}`;
    const found = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) || [];
    const cand = found.find((e) => !NOREPLY_RE.test(e)
        && e.toLowerCase() !== (ownAddress || "").toLowerCase()
        && !/@(example\.|.*\.png|.*\.jpg)/i.test(e));
    return cand || from;
}

function Composer({ account, accounts = [], replyTo, forwardOf, draft, autoAi, onClose, onSent }) {
    const { user } = useAuth();
    // Jul-2026 (User-Wunsch) — Absender-Wechsel NUR im Admin-Bereich:
    const isAdmin = user?.role === "admin";
    const [fromId, setFromId] = useState(draft?.account_id || account.id);
    const fromAcc = accounts.find((a) => a.id === fromId) || account;
    const [to, setTo] = useState(draft ? (draft.to || "") : (replyTo ? bestReplyAddress(replyTo, account?.email_address) : ""));
    const [cc, setCc] = useState(draft?.cc || "");
    const [subject, setSubject] = useState(
        draft ? (draft.subject || "")
            : replyTo ? `Re: ${replyTo.subject || ""}`
                : forwardOf ? `Fwd: ${forwardOf.subject || ""}` : "");
    // Jul-2026 (User-Wunsch) — Weiterleiten: Original als Zitat vorbefüllen.
    const [body, setBody] = useState(draft ? (draft.body || "") : (forwardOf ? [
        "", "",
        "---------- Weitergeleitete Nachricht ----------",
        `Von: ${forwardOf.from_name || ""} <${forwardOf.from_addr || ""}>`.trim(),
        `Datum: ${forwardOf.date ? new Date(forwardOf.date).toLocaleString("de-DE") : ""}`,
        `Betreff: ${forwardOf.subject || ""}`,
        "",
        (forwardOf.body_text || "").trim(),
    ].join("\n") : ""));
    const [draftBusy, setDraftBusy] = useState(false);
    const [busy, setBusy] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiHint, setAiHint] = useState("");
    const [templates, setTemplates] = useState([]);
    const [files, setFiles] = useState([]); // {filename, content_b64, size}
    const [invoices, setInvoices] = useState([]); // ausgewählte Intranet-Dokumente {id, label}
    const [docPicker, setDocPicker] = useState(false);
    const [docQ, setDocQ] = useState("");
    const [docResults, setDocResults] = useState([]);
    // Jul-2026 (User-Wunsch): Live-Vorschau der Branding-Signatur beim Schreiben
    const [sigPreview, setSigPreview] = useState(null);
    useEffect(() => {
        if (fromAcc?.use_standard_signature) {
            api.get("/email/signature/standard").then((r) => setSigPreview(r.data)).catch(() => setSigPreview(null));
        } else {
            setSigPreview(null);
        }
    }, [fromAcc?.id, fromAcc?.use_standard_signature]);

    const addFiles = async (fileList) => {
        const list = Array.from(fileList || []).slice(0, 5 - files.length - invoices.length);
        for (const file of list) {
            if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: max. 5 MB`); continue; }
            const b64 = await new Promise((res, rej) => {
                const rd = new FileReader();
                rd.onload = () => res(String(rd.result).split(",")[1] || "");
                rd.onerror = rej;
                rd.readAsDataURL(file);
            });
            setFiles((p) => [...p, { filename: file.name, content_b64: b64, size: file.size }]);
        }
    };

    const searchDocs = async (query) => {
        setDocQ(query);
        try {
            const r = await api.get(`/email/intranet-docs?q=${encodeURIComponent(query)}`);
            setDocResults(r.data.items || []);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        api.get("/dealer/email-templates").then((r) => {
            const items = (r.data.items || r.data.templates || []).filter((t) => t.enabled !== false);
            setTemplates(items);
        }).catch(() => {});
    }, []);

    const runAi = async (mode) => {
        setAiBusy(true);
        try {
            const r = await api.post("/email/ai", { mode, message_id: replyTo?.id, instructions: mode === "draft" ? body || aiHint : aiHint });
            setBody(r.data.text);
            toast.success("KI-Text eingefügt — bitte prüfen & anpassen");
        } catch (e) { toast.error(e.response?.data?.detail || "KI-Aufruf fehlgeschlagen"); }
        finally { setAiBusy(false); }
    };

    // Jul-2026 (User-Wunsch): Deep-Link „Antworten (KI)" startet den Vorschlag sofort.
    useEffect(() => {
        if (autoAi && replyTo?.id) runAi("reply");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoAi]);

    // Jul-2026 (User-Wunsch) — Entwurf speichern & später weiterschreiben.
    const saveDraft = async () => {
        setDraftBusy(true);
        try {
            await api.post("/email/drafts", { id: draft?.id, account_id: fromId, to, cc: cc.trim(), subject, body });
            toast.success("Entwurf gespeichert — Ordner „Entwürfe“");
            onSent();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setDraftBusy(false); }
    };

    const send = async () => {
        if (!to.trim() || !body.trim()) { toast.error("Empfänger und Text erforderlich"); return; }
        setBusy(true);
        try {
            const r = await api.post("/email/send", {
                account_id: fromId, to, cc: cc.trim(), subject, body, reply_to_message_id: replyTo?.id,
                forward_of_message_id: forwardOf?.id,
                attachments: files.map(({ filename, content_b64 }) => ({ filename, content_b64 })),
                invoice_ids: invoices.map((i) => i.id),
            });
            if (r.data.pending_approval) toast.info("E-Mail wartet auf Freigabe (Menü → E-Mail-Freigabe) und wird danach versendet");
            else toast.success(r.data.mocked ? "Gesendet (MOCK — Resend-Key ausstehend)" : "E-Mail gesendet");
            if (draft?.id) api.delete(`/email/drafts/${draft.id}`).catch(() => {});
            onSent();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="email-composer">
                <DialogHeader>
                    <DialogTitle>{replyTo ? "Antworten" : forwardOf ? "Weiterleiten" : "Neue E-Mail"} — {fromAcc.email_address}</DialogTitle>
                    <DialogDescription className="sr-only">E-Mail verfassen und senden</DialogDescription>
                </DialogHeader>
                <div className="space-y-2.5">
                    {isAdmin && accounts.length > 1 && (
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Absender</label>
                            <select value={fromId} onChange={(e) => setFromId(e.target.value)}
                                className="mt-0.5 w-full h-9 rounded-sm border border-gray-300 text-sm px-2 bg-white"
                                data-testid="email-compose-from-select">
                                {accounts.map((a) => (
                                    <option key={a.id} value={a.id}>{a.email_address}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {forwardOf && (forwardOf.attachments || []).length > 0 && (
                        <div className="text-[11px] font-semibold text-gray-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5" data-testid="email-forward-attachments-note">
                            {(forwardOf.attachments || []).length} Anhang/Anhänge der Original-Mail werden automatisch mitgesendet.
                        </div>
                    )}
                    <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="An (kommagetrennt)" className="rounded-sm" data-testid="email-compose-to" />
                    <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="CC (optional, kommagetrennt)" className="rounded-sm" data-testid="email-compose-cc" />
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Betreff" className="rounded-sm" data-testid="email-compose-subject" />
                    {templates.length > 0 && (
                        <div className="flex items-center gap-2">
                        <select className="flex-1 h-9 rounded-sm border border-gray-300 text-sm px-2" defaultValue=""
                            onChange={(e) => { const t = templates.find((x) => x.key === e.target.value); if (t) { setBody(t.body_text || t.body || ""); if (t.subject && !replyTo) setSubject(t.subject); } }}
                            data-testid="email-compose-template">
                            <option value="">Textvorlage einfügen…</option>
                            {templates.map((t) => <option key={t.key} value={t.key}>{t.label || t.key}</option>)}
                        </select>
                        <button type="button"
                            onClick={() => window.open("/dashboard?tab=settings#settings-email-templates", "_blank", "noopener")}
                            className="h-9 px-2.5 rounded-sm border border-gray-300 text-gray-600 hover:text-[#111827] hover:border-[#111827] inline-flex items-center gap-1 text-xs font-bold"
                            title="Textvorlagen bearbeiten (öffnet Einstellungen)"
                            data-testid="email-compose-template-edit">
                            <PenLine className="w-3.5 h-3.5" /> Bearbeiten
                        </button>
                        </div>
                    )}
                    <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} placeholder="Nachricht… (oder KI nutzen)" className="rounded-sm text-sm" data-testid="email-compose-body" />
                    {sigPreview?.html && (
                        <div className="border border-gray-200 rounded-sm bg-gray-50 p-2" data-testid="email-compose-signature-preview">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Signatur — wird automatisch angehängt</div>
                            <div className="max-h-36 overflow-y-auto bg-white rounded-sm px-2 pb-2 [&>div]:!mt-0 [&>div]:!border-t-0 [&>div]:!pt-2" dangerouslySetInnerHTML={{ __html: sigPreview.html }} />
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 bg-violet-50 border border-violet-200 rounded-sm p-2">
                        <Sparkles className="w-4 h-4 text-violet-600 shrink-0" />
                        <Input value={aiHint} onChange={(e) => setAiHint(e.target.value)} placeholder="Hinweis an die KI (z. B. 'freundlich absagen, Alternativtermin Do 14 Uhr')" className="rounded-sm flex-1 h-8 text-xs" data-testid="email-ai-hint" />
                        {replyTo && (
                            <Button type="button" size="sm" variant="outline" onClick={() => runAi("reply")} disabled={aiBusy} className="h-8 text-xs" data-testid="email-ai-reply">
                                {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "KI-Antwortvorschlag"}
                            </Button>
                        )}
                        <Button type="button" size="sm" variant="outline" onClick={() => runAi("draft")} disabled={aiBusy} className="h-8 text-xs" data-testid="email-ai-draft">
                            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "KI-Entwurf aus Stichpunkten"}
                        </Button>
                    </div>
                    {/* Anhänge: Desktop-Dateien + Intranet-Dokumente (Rechnungen) */}
                    <div className="flex flex-wrap items-center gap-2 border border-gray-200 rounded-sm p-2 bg-gray-50">
                        <Paperclip className="w-4 h-4 text-gray-500 shrink-0" />
                        <label className="inline-flex items-center gap-1 text-xs font-bold cursor-pointer text-gray-700 hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white" data-testid="email-attach-file-btn">
                            <FileUp className="w-3.5 h-3.5" /> Datei anhängen
                            <input type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                        </label>
                        <Button type="button" size="sm" variant="outline" onClick={() => { setDocPicker((v) => !v); if (!docPicker) searchDocs(""); }} className="h-7 text-xs rounded-sm" data-testid="email-attach-intranet-btn">
                            <FileText className="w-3.5 h-3.5 mr-1" /> Aus dem Intranet
                        </Button>
                        <span className="text-[10px] text-gray-400">max. 5 Anhänge, je 5 MB</span>
                        {(files.length > 0 || invoices.length > 0) && (
                            <div className="w-full flex flex-wrap gap-1.5" data-testid="email-attachment-chips">
                                {files.map((f, i) => (
                                    <span key={`f${i}`} className="inline-flex items-center gap-1 text-[10px] font-bold bg-white border border-gray-300 rounded-sm px-1.5 py-0.5">
                                        <Paperclip className="w-3 h-3" /> {f.filename}
                                        <button type="button" onClick={() => setFiles((p) => p.filter((_, x) => x !== i))} className="text-gray-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                    </span>
                                ))}
                                {invoices.map((d, i) => (
                                    <span key={`d${i}`} className="inline-flex items-center gap-1 text-[10px] font-bold bg-blue-50 border border-blue-200 text-blue-800 rounded-sm px-1.5 py-0.5">
                                        <FileText className="w-3 h-3" /> {d.label}
                                        <button type="button" onClick={() => setInvoices((p) => p.filter((_, x) => x !== i))} className="text-blue-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                        {docPicker && (
                            <div className="w-full space-y-1.5" data-testid="email-intranet-picker">
                                <Input value={docQ} onChange={(e) => searchDocs(e.target.value)} placeholder="Rechnung suchen (Nr., Kunde)…" className="h-8 rounded-sm text-xs" data-testid="email-intranet-search" />
                                <div className="max-h-32 overflow-y-auto divide-y divide-gray-100 bg-white border border-gray-200 rounded-sm">
                                    {docResults.length === 0 && <div className="p-2 text-[11px] text-gray-400">Keine Dokumente gefunden</div>}
                                    {docResults.map((d) => (
                                        <button key={d.id} type="button"
                                            onClick={() => { if (!invoices.find((x) => x.id === d.id)) setInvoices((p) => [...p, d]); setDocPicker(false); }}
                                            className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50" data-testid={`email-intranet-doc-${d.id}`}>
                                            {d.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={saveDraft} disabled={busy || draftBusy} className="rounded-sm" data-testid="email-save-draft-btn">
                            {draftBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />} Entwurf speichern
                        </Button>
                        <Button onClick={send} disabled={busy} className="flex-1 bg-[#111827] text-white rounded-sm" data-testid="email-compose-send">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Senden
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ------------------------------ E-Mail-Regeln ----------------------------- */
function RulesDialog({ employees, onClose, initial, accountId }) {
    const [rules, setRules] = useState([]);
    const [f, setF] = useState({ field: initial?.field || "from", contains: initial?.contains || "", set_category: "", assignee_id: "", mark_done: false });
    const [busy, setBusy] = useState(false);
    // Jun-2026 (User-Wunsch) — KI-Vorschläge für wiederkehrende Absender
    const [suggestions, setSuggestions] = useState(null);
    const [sugBusy, setSugBusy] = useState(false);

    const loadSuggestions = async () => {
        if (!accountId || sugBusy) return;
        setSugBusy(true);
        try {
            const r = await api.get("/email/rules/suggestions", { params: { account_id: accountId } });
            setSuggestions(r.data.items || []);
            if (!(r.data.items || []).length) toast.info("Keine wiederkehrenden Absender ohne Regel gefunden");
        } catch (e) { toast.error(e?.response?.data?.detail || "Vorschläge konnten nicht geladen werden"); }
        setSugBusy(false);
    };

    const adoptSuggestion = async (s) => {
        try {
            await api.post("/email/rules", { field: "from", contains: s.contains, set_category: s.set_category || "", mark_done: !!s.mark_done });
            toast.success(`Regel für ${s.contains} angelegt`);
            setSuggestions((p) => (p || []).filter((x) => x.contains !== s.contains));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen"); }
    };

    const load = () => api.get("/email/rules").then((r) => setRules(r.data.items || [])).catch(() => {});
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!f.contains.trim()) { toast.error("Bitte einen Suchbegriff angeben"); return; }
        setBusy(true);
        try {
            const emp = employees.find((e) => e.id === f.assignee_id);
            await api.post("/email/rules", { ...f, assignee_name: emp?.name || "" });
            toast.success("Regel angelegt");
            setF({ field: "from", contains: "", set_category: "", assignee_id: "", mark_done: false });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        try { await api.delete(`/email/rules/${id}`); load(); toast.success("Regel gelöscht"); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xl" data-testid="email-rules-dialog">
                <DialogHeader><DialogTitle>E-Mail-Regeln</DialogTitle></DialogHeader>
                <p className="text-xs text-gray-500 -mt-2">Regeln greifen automatisch beim Posteingang-Abruf: Kategorie setzen, Mitarbeiter zuweisen oder direkt als erledigt markieren.</p>
                <div className="space-y-2 border border-gray-200 rounded-sm p-3 bg-gray-50">
                    <div className="grid grid-cols-2 gap-2">
                        <select value={f.field} onChange={(e) => setF((p) => ({ ...p, field: e.target.value }))} className="h-9 rounded-sm border border-gray-300 text-sm px-2" data-testid="email-rule-field">
                            <option value="from">Absender enthält…</option>
                            <option value="subject">Betreff enthält…</option>
                        </select>
                        <Input value={f.contains} onChange={(e) => setF((p) => ({ ...p, contains: e.target.value }))} placeholder="z. B. newsletter@ oder Rechnung" className="rounded-sm h-9" data-testid="email-rule-contains" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <select value={f.set_category} onChange={(e) => setF((p) => ({ ...p, set_category: e.target.value }))} className="h-9 rounded-sm border border-gray-300 text-sm px-2" data-testid="email-rule-category">
                            <option value="">Kategorie setzen (optional)</option>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select value={f.assignee_id} onChange={(e) => setF((p) => ({ ...p, assignee_id: e.target.value }))} className="h-9 rounded-sm border border-gray-300 text-sm px-2" data-testid="email-rule-assignee">
                            <option value="">Mitarbeiter zuweisen (optional)</option>
                            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                    </div>
                    <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
                        <input type="checkbox" checked={f.mark_done} onChange={(e) => setF((p) => ({ ...p, mark_done: e.target.checked }))} data-testid="email-rule-mark-done" />
                        Direkt als erledigt markieren (z. B. Newsletter)
                    </label>
                    <Button size="sm" onClick={create} disabled={busy} className="bg-[#111827] text-white rounded-sm h-8 text-xs" data-testid="email-rule-create">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Regel anlegen
                    </Button>
                </div>
                {/* KI-Vorschläge (Jun-2026, User-Wunsch) */}
                <div className="border border-[#FFCC00]/60 bg-[#FFFBEB] rounded-sm p-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-black text-[#111827] inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> KI-Vorschläge — wiederkehrende Absender</span>
                        <Button size="sm" variant="outline" onClick={loadSuggestions} disabled={sugBusy || !accountId} className="h-7 text-xs rounded-sm" data-testid="email-rule-suggestions-btn">
                            {sugBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} {suggestions === null ? "Vorschläge laden" : "Neu laden"}
                        </Button>
                    </div>
                    {suggestions !== null && suggestions.length > 0 && (
                        <div className="mt-2 divide-y divide-[#FFCC00]/40 max-h-44 overflow-y-auto" data-testid="email-rule-suggestions-list">
                            {suggestions.map((s) => (
                                <div key={s.contains} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`email-rule-suggestion-${s.contains}`}>
                                    <div className="flex-1 min-w-0">
                                        <b className="truncate block">{s.contains}</b>
                                        <span className="text-gray-600">{s.count} Mails · {s.set_category ? `Kategorie „${s.set_category}"` : ""}{s.set_category && s.mark_done ? " + " : ""}{s.mark_done ? "direkt erledigt" : ""}{!s.set_category && !s.mark_done ? "nur beobachten" : ""}{s.reason ? ` — ${s.reason}` : ""}</span>
                                    </div>
                                    <button type="button" onClick={() => adoptSuggestion(s)}
                                        className="shrink-0 text-[11px] font-bold px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black"
                                        data-testid={`email-rule-adopt-${s.contains}`}>Übernehmen</button>
                                </div>
                            ))}
                        </div>
                    )}
                    {suggestions !== null && suggestions.length === 0 && (
                        <div className="mt-2 text-xs text-gray-500">Keine Vorschläge — alle häufigen Absender sind bereits abgedeckt.</div>
                    )}
                </div>
                <div className="max-h-56 overflow-y-auto divide-y divide-gray-100" data-testid="email-rules-list">
                    {rules.length === 0 && <div className="text-xs text-gray-400 py-3 text-center">Noch keine Regeln</div>}
                    {rules.map((r) => (
                        <div key={r.id} className="flex items-center gap-2 py-2 text-xs" data-testid={`email-rule-${r.id}`}>
                            <div className="flex-1 min-w-0">
                                <b>{r.field === "subject" ? "Betreff" : "Absender"}</b> enthält „{r.contains}“
                                {r.set_category && <> → Kategorie <b>{r.set_category}</b></>}
                                {r.assignee_name && <> → zugewiesen an <b>{r.assignee_name}</b></>}
                                {r.mark_done && <> → <b>erledigt</b></>}
                            </div>
                            <button type="button" onClick={() => remove(r.id)} className="text-gray-400 hover:text-red-600" data-testid={`email-rule-del-${r.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ---------------------------- Signatur bearbeiten ------------------------- */
function SignatureDialog({ account, onClose, onSaved }) {
    const [signature, setSignature] = useState(account.signature || "");
    const [label, setLabel] = useState(account.label || "");
    const [useStd, setUseStd] = useState(!!account.use_standard_signature);
    const [autoLead, setAutoLead] = useState(!!account.auto_lead_enabled);
    const [leadIntakeAll, setLeadIntakeAll] = useState(!!account.lead_intake_all);
    const [backfillBusy, setBackfillBusy] = useState(false);
    const runBackfill = async () => {
        setBackfillBusy(true);
        try {
            const { data } = await api.post(`/email/accounts/${account.id}/lead-backfill`);
            toast.success(`${data.checked} Mails geprüft — ${data.created} neue(r) Lead(s) angelegt${data.already_leads ? ` (${data.already_leads} waren bereits Leads)` : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Backfill fehlgeschlagen");
        } finally { setBackfillBusy(false); }
    };
    const [leadDealerId, setLeadDealerId] = useState(account.auto_lead_dealer_id || "");
    // Jul-2026 (User-Wunsch „nach Region bündeln") — mehrere Ziel-Standorte möglich.
    const [leadDealerIds, setLeadDealerIds] = useState(
        Array.isArray(account.auto_lead_dealer_ids) && account.auto_lead_dealer_ids.length
            ? account.auto_lead_dealer_ids
            : (account.auto_lead_dealer_id ? [account.auto_lead_dealer_id] : [])
    );
    const [dealers, setDealers] = useState(null); // null = kein Admin / nicht geladen
    // Jul-2026 (User-Wunsch) — IMAP-Ordner ansehen & ändern.
    const [imapFolder, setImapFolder] = useState(account.imap_folder || "INBOX");
    const [folders, setFolders] = useState(null);
    // Aug-2026 (User-Wunsch) — Weiterleitung nachträglich änderbar.
    const [forwardTo, setForwardTo] = useState(account.forward_to || "");
    const [stdPreview, setStdPreview] = useState("");
    const [stdHtml, setStdHtml] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/email/signature/standard")
            .then((r) => { setStdPreview(r.data?.text || ""); setStdHtml(r.data?.html || ""); })
            .catch(() => { setStdPreview(""); setStdHtml(""); });
        // Nur für Admins: Händler-Liste für die Lead-Zuweisung (403 → ausblenden)
        api.get("/admin/dealers", { params: { limit: 200 } })
            .then((r) => setDealers(r.data?.dealers || r.data || []))
            .catch(() => setDealers(null));
        api.get(`/email/accounts/${account.id}/folders`)
            .then((r) => setFolders(r.data?.folders || null))
            .catch(() => setFolders(null));
    }, [account.id]);
    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/email/accounts/${account.id}`, {
                signature, label, use_standard_signature: useStd,
                auto_lead_enabled: autoLead, lead_intake_all: leadIntakeAll,
                auto_lead_dealer_id: leadDealerIds[0] || leadDealerId || null,
                auto_lead_dealer_ids: leadDealerIds,
                forward_to: forwardTo,
                ...(account.imap_host ? { imap_folder: imapFolder || "INBOX" } : {}),
            });
            toast.success("Gespeichert");
            onSaved();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="email-signature-dialog">
                <DialogHeader>
                    <DialogTitle>Postfach bearbeiten — {account.email_address}</DialogTitle>
                    <DialogDescription className="sr-only">Bezeichnung, Signatur, Weiterleitung und Auto-Lead des Postfachs bearbeiten</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-bold text-gray-600">Bezeichnung</label>
                        <Input value={label} onChange={(e) => setLabel(e.target.value)} className="rounded-sm" data-testid="email-signature-label" />
                    </div>
                    {account.imap_host ? (
                        <div data-testid="email-imap-folder-setting">
                            <label className="text-xs font-bold text-gray-600">Posteingangs-Ordner (IMAP)</label>
                            {Array.isArray(folders) && folders.length > 0 ? (
                                <select value={imapFolder} onChange={(e) => setImapFolder(e.target.value)}
                                    className="w-full h-9 rounded-sm border border-gray-300 bg-white px-2 text-sm" data-testid="email-imap-folder-select">
                                    {!folders.includes(imapFolder) && <option value={imapFolder}>{imapFolder}</option>}
                                    {folders.map((f) => <option key={f} value={f}>{f}</option>)}
                                </select>
                            ) : (
                                <Input value={imapFolder} onChange={(e) => setImapFolder(e.target.value)} className="rounded-sm" placeholder="INBOX" data-testid="email-imap-folder-input" />
                            )}
                            <p className="text-[10px] text-gray-400 mt-0.5">Aus diesem Ordner werden neue Mails abgeholt. Nach einem Wechsel wird der Ordner komplett neu eingelesen.</p>
                        </div>
                    ) : null}
                    <div data-testid="email-forward-setting">
                        <label className="text-xs font-bold text-gray-600">Weiterleitung an (E-Mail, optional)</label>
                        <Input value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} className="rounded-sm" placeholder="z. B. verkauf@firma.de — leer = keine Weiterleitung" data-testid="email-forward-input" />
                        <p className="text-[10px] text-gray-400 mt-0.5">Jede eingehende Mail dieses Postfachs wird zusätzlich an diese Adresse weitergeleitet. Feld leeren, um die Weiterleitung zu entfernen.</p>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer bg-[#FFFBEB] border border-[#FFCC00]/50 rounded-sm p-2.5" data-testid="email-signature-standard-toggle">
                        <input type="checkbox" checked={useStd} onChange={(e) => setUseStd(e.target.checked)} className="w-4 h-4 mt-0.5" data-testid="email-signature-standard-cb" />
                        <span className="text-sm">
                            <span className="font-bold">Standard-Signatur verwenden</span>
                            <span className="block text-[11px] text-gray-600">Wird automatisch aus Standort (Firma, Anschrift, Tel., Öffnungszeiten) und dem jeweils eingeloggten Mitarbeiter (Name, Funktion, Durchwahl) erzeugt — immer aktuell, keine Pflege nötig.</span>
                        </span>
                    </label>
                    {useStd ? (
                        <div>
                            <label className="text-xs font-bold text-gray-600">Vorschau (für dich als eingeloggten Nutzer)</label>
                            {stdHtml ? (
                                <div className="bg-white border border-gray-200 rounded-sm px-2.5 pb-2.5 max-h-56 overflow-y-auto [&>div]:!mt-0 [&>div]:!pt-2.5" data-testid="email-signature-standard-preview" dangerouslySetInnerHTML={{ __html: stdHtml }} />
                            ) : (
                                <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded-sm p-2.5 whitespace-pre-wrap font-sans text-gray-700" data-testid="email-signature-standard-preview">{stdPreview || "…"}</pre>
                            )}
                        </div>
                    ) : (
                        <div>
                            <label className="text-xs font-bold text-gray-600">Signatur (wird unter jede E-Mail gesetzt)</label>
                            <Textarea value={signature} onChange={(e) => setSignature(e.target.value)} rows={5} className="rounded-sm text-sm" placeholder={"Mit freundlichen Grüßen\nMax Mustermann\nTel. 030 …"} data-testid="email-signature-text" />
                        </div>
                    )}
                    <label className="flex items-start gap-2 cursor-pointer bg-emerald-50 border border-emerald-200 rounded-sm p-2.5" data-testid="email-autolead-toggle">
                        <input type="checkbox" checked={autoLead} onChange={(e) => setAutoLead(e.target.checked)} className="w-4 h-4 mt-0.5" data-testid="email-autolead-cb" />
                        <span className="text-sm">
                            <span className="font-bold">Kaufanfragen automatisch als Lead anlegen</span>
                            <span className="block text-[11px] text-gray-600">Eingehende Mails werden per KI auf Kaufinteresse geprüft. Erkannte Anfragen werden automatisch als Lead angelegt — inkl. Fahrzeugbezug, wenn ein passendes Inserat im Bestand gefunden wird.</span>
                        </span>
                    </label>
                    {autoLead && (
                        <div className="ml-1 pl-4 border-l-2 border-emerald-200 space-y-2">
                            <label className="flex items-start gap-2 cursor-pointer" data-testid="email-leadintake-toggle">
                                <input type="checkbox" checked={leadIntakeAll} onChange={(e) => setLeadIntakeAll(e.target.checked)} className="w-4 h-4 mt-0.5" data-testid="email-leadintake-cb" />
                                <span className="text-sm">
                                    <span className="font-bold">Dediziertes Lead-Postfach: JEDE eingehende Mail wird zum Lead</span>
                                    <span className="block text-[11px] text-gray-600">Ohne KI-Vorfilter — für Adressen, die ausschließlich Fahrzeug-Anfragen empfangen (z. B. rollerhaus-koenig@twowheels.info). Verkäufer-Zuteilung nach den hinterlegten Lead-Regeln.</span>
                                </span>
                            </label>
                            {Array.isArray(dealers) && dealers.length > 0 && (
                                <div>
                                    <label className="text-xs font-bold text-gray-600">Leads zuweisen an Händler / Standorte</label>
                                    <p className="text-[10px] text-gray-500 mb-1">
                                        Mehrfachauswahl möglich (nach Region bündeln): Passt die Stadt/PLZ eines gewählten
                                        Standorts zur Anfrage, geht der Lead dorthin — sonst faire Verteilung im Wechsel.
                                        Keine Auswahl = Konto-Inhaber.
                                    </p>
                                    <div className="max-h-44 overflow-y-auto border border-gray-300 rounded-sm bg-white divide-y divide-gray-100" data-testid="email-leaddealer-multiselect">
                                        {[...dealers].sort((a, b) => `${a.company_name || a.name || a.email}`.localeCompare(`${b.company_name || b.name || b.email}`)).map((d) => {
                                            const addr = [d.street || d.address, d.city].filter(Boolean).join(", ");
                                            const checked = leadDealerIds.includes(d.id);
                                            return (
                                                <label key={d.id} className={`flex items-start gap-2 px-2 py-1.5 cursor-pointer text-[12px] ${checked ? "bg-emerald-50" : "hover:bg-gray-50"}`} data-testid={`email-leaddealer-opt-${d.id}`}>
                                                    <input type="checkbox" checked={checked} className="w-3.5 h-3.5 mt-0.5"
                                                        onChange={(e) => setLeadDealerIds((prev) => e.target.checked
                                                            ? [...prev, d.id]
                                                            : prev.filter((x) => x !== d.id))} />
                                                    <span>
                                                        <span className="font-bold">{d.company_name || d.name || d.email}</span>
                                                        {addr ? <span className="text-gray-500"> — {addr}</span> : null}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {leadDealerIds.length > 0 && (
                                        <p className="text-[10px] text-emerald-700 font-bold mt-1" data-testid="email-leaddealer-count">
                                            {leadDealerIds.length} Standort(e) ausgewählt
                                        </p>
                                    )}
                                </div>
                            )}
                            {/* Aug-2026 (User-Wunsch) — Lead-Backfill: letzte 50 Mails rückwirkend prüfen */}
                            {account.auto_lead_enabled ? (
                                <Button type="button" variant="outline" disabled={backfillBusy} onClick={runBackfill}
                                    className="w-full h-9 rounded-sm border-emerald-300 text-emerald-800 font-bold text-xs"
                                    data-testid="email-lead-backfill-btn">
                                    {backfillBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    Letzte 50 Mails jetzt auf verpasste Anfragen prüfen
                                </Button>
                            ) : (
                                <p className="text-[10px] text-gray-400">Nach dem Speichern kannst du hier die letzten 50 Mails rückwirkend auf Anfragen prüfen lassen.</p>
                            )}
                        </div>
                    )}
                    <Button onClick={save} disabled={busy} className="w-full bg-[#111827] text-white rounded-sm" data-testid="email-signature-save">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Speichern
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* --------------------------------- Seite --------------------------------- */
export default function EmailClientPage() {
    const gearsOk = useGearsVisible();

    const [accounts, setAccounts] = useState([]);
    const [active, setActive] = useState(null);
    const [folder, setFolder] = useState("inbox"); // inbox | sent | reminders
    const [messages, setMessages] = useState([]);
    const [selected, setSelected] = useState(null);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [connect, setConnect] = useState(false);
    const [compose, setCompose] = useState(null); // {replyTo?, autoAi?}
    const [dueCount, setDueCount] = useState(0);
    const [rulesOpen, setRulesOpen] = useState(false);
    const [sigEdit, setSigEdit] = useState(null); // account
    const [employees, setEmployees] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    // Jul-2026 (User-Wunsch) — KI-Zusammenfassung aller Inhalte zur Absender-Adresse.
    const [senderSummary, setSenderSummary] = useState(null); // {busy, text, sender}
    const runSenderSummary = async () => {
        if (!selected) return;
        setSenderSummary({ busy: true, text: "", sender: selected.from_addr });
        try {
            const r = await api.post("/email/ai", { mode: "sender_summary", message_id: selected.id });
            setSenderSummary({ busy: false, text: r.data?.text || "", sender: selected.from_addr });
        } catch (e) {
            setSenderSummary(null);
            toast.error(e.response?.data?.detail || "KI-Zusammenfassung fehlgeschlagen");
        }
    };

    useEffect(() => {
        api.get("/me/tasks/assignable-employees").then((r) => setEmployees(r.data.items || [])).catch(() => {});
    }, []);

    const loadAccounts = useCallback(async () => {
        try {
            const r = await api.get("/email/accounts");
            setAccounts(r.data.items || []);
            // Aug-2026 (User-Meldung „mir werden keine E-Mails angezeigt"):
            // standardmäßig das ECHTE Postfach (IMAP/M365) öffnen — nicht die
            // System-Adresse ohne Empfang, die zufällig zuerst in der Liste steht.
            if (!active && (r.data.items || []).length) {
                const list = r.data.items || [];
                setActive(list.find((a) => a.type !== "system") || list[0]);
            }
        } catch { /* ignore */ }
    }, [active]);

    const loadMessages = useCallback(async () => {
        if (!active) return;
        setLoading(true);
        try {
            // Jul-2026 (User-Wunsch): Entwürfe kommen aus einer eigenen Sammlung.
            if (folder === "drafts") {
                const r = await api.get(`/email/drafts?account_id=${active.id}`);
                setMessages((r.data.items || []).map((d) => ({
                    ...d, is_draft: true, read: true,
                    from_name: d.to || "(kein Empfänger)", date: d.updated_at,
                    subject: d.subject || "(kein Betreff)",
                })));
                return;
            }
            // Jul-2026 (User-Wunsch): Posteingang zeigt nur OFFENE Mails —
            // erledigte wandern in den Ordner „Erledigt".
            let view = "all", f = folder;
            if (folder === "reminders") { view = "reminders"; f = "inbox"; }
            else if (folder === "leads") { view = "leads"; f = "inbox"; }
            else if (folder === "inbox") { view = "open"; }
            else if (folder === "done") { view = "done"; f = "inbox"; }
            else if (folder === "archive") { view = "all"; f = "archive"; }
            const r = await api.get(`/email/messages?account_id=${active.id}&folder=${f}&view=${view}&q=${encodeURIComponent(q)}`);
            setMessages(r.data.items || []);
        } catch (e) { toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"); }
        finally { setLoading(false); }
    }, [active, folder, q]);

    useEffect(() => { loadAccounts(); }, [loadAccounts]);
    useEffect(() => { loadMessages(); setSelected(null); }, [loadMessages]);
    useEffect(() => {
        api.get("/email/reminders/due").then((r) => setDueCount(r.data.count || 0)).catch(() => {});
    }, [messages]);

    const sync = async () => {
        if (!active) return;
        setSyncing(true);
        try {
            const r = await api.post(`/email/accounts/${active.id}/sync`);
            if (r.data.demo) toast.success(r.data.message);
            else if (r.data.mocked) toast.info(r.data.message);
            else toast.success(`${r.data.new} neue E-Mail(s) abgerufen`);
            await loadMessages();
            await loadAccounts();
        } catch (e) { toast.error(e.response?.data?.detail || "Abruf fehlgeschlagen"); }
        finally { setSyncing(false); }
    };

    // Aug-2026 (User-Wunsch) — Verbindungstest je Postfach (Login + Ordner)
    const testAccount = async (a) => {
        const tid = toast.loading(`Teste Verbindung: ${a.email_address} …`);
        try {
            const { data: r } = await api.post(`/email/accounts/${a.id}/test`);
            toast.dismiss(tid);
            if (r.kind === "system") { toast.info(r.message, { duration: 8000 }); return; }
            if (r.ok) {
                const parts = [];
                if (r.imap?.ok) parts.push(`Empfang ok (${r.imap.folder}${typeof r.imap.messages === "number" ? `, ${r.imap.messages} Mails` : ""})`);
                if (r.imap?.skipped) parts.push("Empfang übersprungen (Nur-Senden)");
                if (r.smtp?.ok) parts.push("Versand (SMTP) ok");
                if (r.smtp?.skipped) parts.push("SMTP nicht konfiguriert");
                toast.success(`Verbindung ok — ${parts.join(" · ")}`, { duration: 8000 });
            } else {
                const err = r.imap?.error || r.smtp?.error || "Unbekannter Fehler";
                toast.error(`Verbindungstest fehlgeschlagen: ${err}`, { duration: 10000 });
            }
            loadAccounts();
        } catch (e) { toast.dismiss(tid); toast.error(e.response?.data?.detail || "Test fehlgeschlagen"); }
    };

    const openMessage = async (m) => {
        try {
            const r = await api.get(`/email/messages/${m.id}`);
            setSelected(r.data);
            setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
        } catch { toast.error("Nachricht konnte nicht geladen werden"); }
    };

    // Jul-2026 (User-Wunsch) — Deep-Link aus dem Dashboard: ?connect=1 öffnet
    // direkt den „Postfach verbinden"-Dialog (spart den Zwischenschritt).
    useEffect(() => {
        if (searchParams.get("connect") === "1") {
            setConnect(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Jul-2026 (User-Wunsch) — Deep-Link aus dem Cockpit: ?open={id}&ai=1 öffnet
    // die Mail direkt und startet den KI-Antwortvorschlag.
    useEffect(() => {
        const openId = searchParams.get("open");
        if (!openId || accounts.length === 0) return;
        (async () => {
            try {
                const r = await api.get(`/email/messages/${openId}`);
                const acc = accounts.find((a) => a.id === r.data.account_id);
                if (acc) setActive(acc);
                setFolder("inbox");
                setSelected(r.data);
                if (searchParams.get("ai") === "1") {
                    setCompose({ replyTo: r.data, autoAi: true });
                }
            } catch { toast.error("Verlinkte E-Mail nicht gefunden"); }
            finally { setSearchParams({}, { replace: true }); }
        })();
    }, [searchParams, accounts, setSearchParams]);

    const deleteDraft = async (id) => {
        if (!window.confirm("Entwurf löschen?")) return;
        try { await api.delete(`/email/drafts/${id}`); toast.success("Entwurf gelöscht"); loadMessages(); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };

    const patchSelected = async (patch) => {
        if (!selected) return;
        try {
            await api.patch(`/email/messages/${selected.id}`, patch);
            setSelected((p) => ({ ...p, ...patch }));
            loadMessages();
            if ("reminder_at" in patch) toast.success(patch.reminder_at ? "Wiedervorlage gesetzt" : "Wiedervorlage entfernt");
        } catch { toast.error("Aktion fehlgeschlagen"); }
    };

    const setReminder = () => {
        const days = window.prompt("Wiedervorlage in wie vielen Tagen? (z. B. 3)", "3");
        if (!days) return;
        const d = new Date(); d.setDate(d.getDate() + (parseInt(days, 10) || 3));
        patchSelected({ reminder_at: d.toISOString() });
    };

    // Jun-2026 (User-Wunsch) — Lead aus E-Mail erzeugen & optional direkt zuweisen
    const [leadBusy, setLeadBusy] = useState(false);
    const createLeadFromMail = async (assigneeId) => {
        if (!selected || leadBusy) return;
        setLeadBusy(true);
        try {
            const r = await api.post(`/email/messages/${selected.id}/create-lead`, { assignee_id: assigneeId || null });
            setSelected((p) => ({ ...p, lead_id: r.data.lead_id }));
            loadMessages();
            toast.success(r.data.existing ? "Lead existiert bereits zu dieser E-Mail"
                : `Lead erzeugt${r.data.assigned_seller_name ? ` — zugewiesen an ${r.data.assigned_seller_name}` : ""}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Lead konnte nicht erzeugt werden"); }
        setLeadBusy(false);
    };

    const removeAccount = async (acc) => {
        if (!window.confirm(`Postfach „${acc.label}" (${acc.email_address}) inkl. lokaler Nachrichten entfernen?`)) return;
        try {
            await api.delete(`/email/accounts/${acc.id}`);
            setAccounts((p) => p.filter((a) => a.id !== acc.id));
            if (active?.id === acc.id) { setActive(null); setMessages([]); }
            toast.success("Postfach entfernt");
        } catch (e) { toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen"); }
    };

    return (
        <div className="max-w-[1400px] mx-auto px-4 py-6" data-testid="email-client-page">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <Mail className="w-6 h-6 text-[#FFCC00]" />
                    <h1 className="font-display font-black text-2xl">E-Mail-Center</h1>
                    {dueCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid="email-due-badge">
                            <BellRing className="w-3 h-3" /> {dueCount} Wiedervorlage(n) fällig
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {active && (
                        <Button size="sm" onClick={() => setCompose({})} className="bg-[#111827] text-white rounded-sm h-9" data-testid="email-new-btn">
                            <Plus className="w-4 h-4 mr-1" /> Neue E-Mail
                        </Button>
                    )}
                    {gearsOk && <Button size="sm" variant="outline" onClick={() => setRulesOpen(true)} className="rounded-sm h-9" data-testid="email-rules-btn">
                        <Settings2 className="w-4 h-4 mr-1" /> Regeln
                    </Button>}
                    <Button size="sm" variant="outline" onClick={() => setConnect(true)} className="rounded-sm h-9" data-testid="email-connect-btn">
                        <Plug2 /> Postfach verbinden
                    </Button>
                </div>
            </div>

            {accounts.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-10 text-center" data-testid="email-empty-state">
                    <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <div className="font-display font-black text-lg">Noch kein Postfach verbunden</div>
                    <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">Verbinden Sie Ihr bestehendes Postfach (IMAP — z. B. IONOS, Strato, Outlook 365) oder legen Sie eine System-Adresse an. Danach: Posteingang, Antworten mit KI, Vorlagen & Wiedervorlagen — alles hier im System.</p>
                    <Button onClick={() => setConnect(true)} className="mt-4 bg-[#111827] text-white rounded-sm" data-testid="email-empty-connect-btn">
                        <Plus className="w-4 h-4 mr-1" /> Postfach verbinden
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[240px_360px_1fr] gap-3 items-start">
                    {/* Konten + Ordner */}
                    <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-3">
                        {/* Jul-2026 (User-Wunsch) — Postfach-Gesundheit: Warnung bei Sync-Problemen. */}
                        {(() => {
                            const isErr = (a) => String(a.status || "").startsWith("error");
                            const errText = (a) => a.sync_error
                                || (String(a.status || "").startsWith("error:") ? String(a.status).slice(7) : "");
                            const sick = (accounts || []).filter((a) => isErr(a)
                                || (a.imap_host && (!a.last_sync_at || (Date.now() - new Date(a.last_sync_at).getTime()) > 24 * 3600 * 1000)));
                            if (!sick.length) return null;
                            return (
                                <div className="bg-red-50 border border-red-200 rounded-sm px-2.5 py-2" data-testid="email-health-warning">
                                    <div className="text-[11px] font-bold text-red-700">⚠ Postfach-Problem{sick.length > 1 ? "e" : ""}</div>
                                    {sick.map((a) => (
                                        <div key={a.id} className="text-[10px] text-red-600 mt-0.5" data-testid={`email-health-item-${a.id}`}>
                                            <b>{a.email_address}</b>: {isErr(a)
                                                ? `Sync-Fehler${errText(a) ? ` — ${errText(a).slice(0, 160)}` : ""}`
                                                : a.last_sync_at
                                                    ? `seit ${Math.round((Date.now() - new Date(a.last_sync_at).getTime()) / 3600000)} Std. nicht synchronisiert`
                                                    : "noch nie synchronisiert"}
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Postfächer</div>
                        {accounts.map((a) => (
                            <div key={a.id} className={`group flex items-center gap-2 rounded-sm border px-2 py-1.5 cursor-pointer ${active?.id === a.id ? "border-[#FFCC00] bg-[#FFFBEB]" : "border-gray-200 hover:bg-gray-50"}`}
                                onClick={() => setActive(a)} data-testid={`email-account-${a.id}`}>
                                <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-bold truncate">{a.label}{!a.is_own && <span className="text-gray-400 font-normal"> · {a.user_label}</span>}</div>
                                    <div className="text-[10px] text-gray-500 flex items-center gap-1">
                                        <span className="truncate">{a.email_address}</span>
                                        {a.usage_info && (
                                            <span onClick={(e) => e.stopPropagation()} data-testid={`email-account-usage-info-${a.id}`}>
                                                <InfoHint id={`email.usage.${a.email_address}`} defaultText={a.usage_info} side="right" />
                                            </span>
                                        )}
                                    </div>
                                    {/* Jul-2026 (User-Wunsch) — Lead-Routing ohne Dialog sichtbar */}
                                    {a.lead_routing && (
                                        <div className="text-[9px] text-emerald-700 font-bold truncate flex items-center gap-0.5"
                                            title={`${a.lead_routing.mode === "alle" ? "Jede Mail wird zum Lead" : "KI erkennt Kaufanfragen"} → ${a.lead_routing.targets.join(", ")}`}
                                            data-testid={`email-account-leadinfo-${a.id}`}>
                                            <Zap className="w-2.5 h-2.5 shrink-0" />
                                            {a.lead_routing.mode === "alle" ? "Alle Mails" : "KI"} → {a.lead_routing.targets.join(", ")}
                                        </div>
                                    )}
                                    {/* Aug-2026 (Prod-Debug) — Sync-Status je Postfach sichtbar machen */}
                                    {a.last_sync_error ? (
                                        <div className="text-[9px] text-red-600 font-bold truncate flex items-center gap-0.5"
                                            title={`Letzter Abruf fehlgeschlagen: ${a.last_sync_error}`}
                                            data-testid={`email-account-syncerror-${a.id}`}>
                                            <AlertTriangle className="w-2.5 h-2.5 shrink-0" /> {a.last_sync_error}
                                        </div>
                                    ) : a.type === "system" ? (
                                        <div className="text-[9px] text-amber-700 truncate" title="System-Adresse: echter E-Mail-Empfang ist hier noch nicht aktiv. Bitte als IMAP-Postfach (z. B. UDAG) verbinden, damit Mails ankommen."
                                            data-testid={`email-account-systemhint-${a.id}`}>
                                            Kein Empfang (System-Adresse) — als IMAP verbinden
                                        </div>
                                    ) : a.last_sync_at ? (
                                        <div className="text-[9px] text-gray-400 truncate" data-testid={`email-account-synctime-${a.id}`}>
                                            Sync {new Date(a.last_sync_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                                        </div>
                                    ) : (
                                        <div className="text-[9px] text-amber-700 truncate" data-testid={`email-account-nosync-${a.id}`}>
                                            Noch kein Abruf — ↻ klicken
                                        </div>
                                    )}
                                </div>
                                {a.unread > 0 && <span className="text-[10px] font-black bg-[#FFCC00] rounded-full px-1.5 py-0.5">{a.unread}</span>}
                                <button type="button" onClick={(e) => { e.stopPropagation(); testAccount(a); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-emerald-600" title="Verbindung testen (Login + Ordner)" data-testid={`email-account-test-${a.id}`}>
                                    <PlugZap className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setSigEdit(a); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-800" title="Signatur/Bezeichnung bearbeiten" data-testid={`email-account-edit-${a.id}`}>
                                    <PenLine className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={(e) => { e.stopPropagation(); removeAccount(a); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600" data-testid={`email-account-del-${a.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 pt-2">Ordner</div>
                        {[["inbox", "Posteingang", Inbox], ["leads", "Leads", Zap], ["done", "Erledigt", CheckCheck], ["archive", "Archiv", Archive], ["sent", "Gesendet", SendHorizonal], ["drafts", "Entwürfe", FileText], ["reminders", "Wiedervorlagen", BellRing]].map(([key, label, Icon]) => (
                            <button key={key} type="button" onClick={() => setFolder(key)}
                                className={`w-full flex items-center gap-2 text-left text-xs font-bold rounded-sm px-2 py-1.5 ${folder === key ? "bg-[#111827] text-white" : "hover:bg-gray-100 text-gray-700"}`}
                                data-testid={`email-folder-${key}`}>
                                <Icon className="w-3.5 h-3.5" /> {label}
                            </button>
                        ))}
                    </div>

                    {/* Nachrichtenliste */}
                    <div className="bg-white border border-gray-200 rounded-sm">
                        <div className="p-2 border-b border-gray-100 flex items-center gap-2">
                            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="h-8 rounded-sm text-xs" data-testid="email-search" />
                            <Button size="sm" variant="outline" onClick={sync} disabled={syncing} className="h-8 rounded-sm shrink-0" data-testid="email-sync-btn">
                                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            </Button>
                        </div>
                        <div className="max-h-[65vh] overflow-y-auto divide-y divide-gray-100">
                            {loading && <div className="p-6 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
                            {!loading && messages.length === 0 && (
                                <div className="p-6 text-center text-xs text-gray-400" data-testid="email-list-empty">
                                    {active?.type === "system" && folder === "inbox"
                                        ? "System-Postfach: Empfang startet mit der Resend-Freischaltung. Versand & KI funktionieren bereits."
                                        : "Keine Nachrichten. Mit ↻ neue E-Mails abrufen."}
                                </div>
                            )}
                            {messages.map((m) => (
                                <button key={m.id} type="button" onClick={() => (m.is_draft ? setCompose({ draft: m }) : openMessage(m))}
                                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 ${selected?.id === m.id ? "bg-[#FFFBEB]" : ""}`}
                                    data-testid={`email-msg-${m.id}`}>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs truncate flex-1 ${!m.read ? "font-black" : "font-medium text-gray-700"}`}>{m.from_name || m.from_addr || m.to}</span>
                                        <span className="text-[10px] text-gray-400 shrink-0">{fmtDate(m.date)}</span>
                                        {m.is_draft && (
                                            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); deleteDraft(m.id); }}
                                                className="text-gray-300 hover:text-red-600 shrink-0" title="Entwurf löschen" data-testid={`email-draft-del-${m.id}`}>
                                                <Trash2 className="w-3 h-3" />
                                            </span>
                                        )}
                                    </div>
                                    <div className={`text-xs truncate ${!m.read ? "font-bold" : "text-gray-600"}`}>{m.subject}</div>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        {m.is_draft && <span className="text-[9px] font-bold bg-gray-100 text-gray-600 border border-gray-300 rounded-sm px-1">Entwurf</span>}
                                        {m.lead_id && (
                                            <span role="link" tabIndex={0}
                                                onClick={(e) => { e.stopPropagation(); window.open(`/dealer/leads?lead=${m.lead_id}`, "_self"); }}
                                                className="text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-sm px-1 inline-flex items-center gap-0.5 hover:bg-emerald-100 cursor-pointer underline-offset-2 hover:underline"
                                                title={`Aus dieser E-Mail wurde ein Lead erstellt${m.lead_seller_name ? ` — zugeteilt an ${m.lead_seller_name}` : ""} — zum Lead springen`}
                                                data-testid={`email-lead-badge-${m.id}`}>
                                                <Zap className="w-2.5 h-2.5" />Lead erstellt →
                                                {m.lead_reaction_min != null
                                                    ? <span className="text-emerald-600 font-medium">· Reaktion {fmtDur(m.lead_reaction_min)}</span>
                                                    : (m.lead_seller_name ? <span className="text-amber-600 font-medium">· noch keine Reaktion</span> : null)}
                                            </span>
                                        )}
                                        {m.pinned && <span className="text-[9px] font-bold bg-[#FFCC00]/30 text-[#111827] border border-[#FFCC00] rounded-sm px-1 inline-flex items-center gap-0.5" data-testid={`email-pinned-badge-${m.id}`}><Pin className="w-2.5 h-2.5" />angepinnt</span>}
                                        {m.demo && <span className="text-[9px] font-bold bg-violet-50 text-violet-700 border border-violet-200 rounded-sm px-1">Demo</span>}
                                        {m.pending_approval && !m.approval_discarded && <span className="text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-1">wartet auf Freigabe</span>}
                                        {m.approval_discarded && <span className="text-[9px] font-bold bg-red-50 text-red-700 border border-red-200 rounded-sm px-1">verworfen — nicht versendet</span>}
                                        {m.customer_label && <span className="text-[9px] font-bold bg-blue-50 text-blue-700 border border-blue-200 rounded-sm px-1">Kunde: {m.customer_label}</span>}
                                        {m.category && <span className="text-[9px] font-bold bg-violet-50 text-violet-700 border border-violet-200 rounded-sm px-1 inline-flex items-center gap-0.5"><Tag className="w-2.5 h-2.5" />{m.category}</span>}
                                        {m.assignee_name && <span className="text-[9px] font-bold bg-teal-50 text-teal-700 border border-teal-200 rounded-sm px-1 inline-flex items-center gap-0.5"><UserPlus className="w-2.5 h-2.5" />{m.assignee_name}</span>}
                                        {m.reminder_at && (new Date(m.reminder_at) <= new Date() && !m.done
                                            ? <span className="text-[9px] font-black bg-red-50 text-red-700 border border-red-300 rounded-sm px-1 inline-flex items-center gap-0.5" data-testid={`email-reminder-due-${m.id}`}><BellRing className="w-2.5 h-2.5" />Wiedervorlage fällig — erneut vorgelegt</span>
                                            : <span className="text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-1 inline-flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{fmtDate(m.reminder_at)}</span>)}
                                        {m.optin_confirmed && <span className="text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-sm px-1" data-testid={`email-optin-badge-${m.id}`} title={`Opt-in-Link automatisch bestätigt: ${m.optin_url || ""}`}>Opt-in automatisch bestätigt ✓</span>}
                                        {m.optin_confirmed === false && m.optin_error && <span className="text-[9px] font-bold bg-red-50 text-red-700 border border-red-200 rounded-sm px-1" title={m.optin_error}>Opt-in-Bestätigung fehlgeschlagen</span>}
                                        {m.done && <span className="text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-1">erledigt</span>}
                                        {(m.attachments || []).length > 0 && <Paperclip className="w-3 h-3 text-gray-400" />}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Lesebereich */}
                    <div className="bg-white border border-gray-200 rounded-sm min-h-[300px]">
                        {!selected ? (
                            <div className="p-10 text-center text-gray-400 text-sm" data-testid="email-reader-empty">
                                <FileText className="w-10 h-10 mx-auto mb-2 text-gray-200" /> Nachricht auswählen
                            </div>
                        ) : (
                            <div data-testid="email-reader">
                                <div className="p-4 border-b border-gray-100">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-display font-black text-base truncate">{selected.subject}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">{selected.from_name} &lt;{selected.from_addr}&gt; · {fmtDate(selected.date)}</div>
                                            {selected.customer_label && <div className="text-[10px] font-bold text-blue-700 mt-1">Zugeordneter Kunde: {selected.customer_label}</div>}
                                        </div>
                                        <button type="button" onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
                                    </div>
                                    {selected.crm_summary && (
                                        <div className="mt-2 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5" data-testid="email-crm-summary">
                                            CRM automatisch aktualisiert: {selected.crm_summary}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                        <Button size="sm" onClick={() => setCompose({ replyTo: selected })} className="h-8 text-xs bg-[#111827] text-white rounded-sm" data-testid="email-reply-btn">
                                            <Reply className="w-3.5 h-3.5 mr-1" /> Antworten (mit KI)
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setCompose({ forwardOf: selected })} className="h-8 text-xs rounded-sm" data-testid="email-forward-btn">
                                            <Forward className="w-3.5 h-3.5 mr-1" /> Weiterleiten
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={setReminder} className="h-8 text-xs rounded-sm" data-testid="email-reminder-btn">
                                            <Clock className="w-3.5 h-3.5 mr-1" /> Wiedervorlage
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => patchSelected({ pinned: !selected.pinned })}
                                                className={`h-8 text-xs rounded-sm ${selected.pinned ? "border-[#FFCC00] bg-[#FFFBEB]" : ""}`} data-testid="email-pin-btn">
                                            <Pin className="w-3.5 h-3.5 mr-1" /> {selected.pinned ? "Lospinnen" : "Anpinnen"}
                                        </Button>
                                        {gearsOk && <Button size="sm" variant="outline" onClick={() => setRulesOpen({ field: "from", contains: selected.from_addr || "" })}
                                                className="h-8 text-xs rounded-sm" title="Regel für diesen Absender anlegen (Kategorie/Zuweisung/Erledigt)" data-testid="email-rule-from-mail-btn">
                                            <Settings2 className="w-3.5 h-3.5 mr-1" /> Regel aus E-Mail
                                        </Button>}
                                        <Button size="sm" variant="outline" onClick={runSenderSummary} disabled={senderSummary?.busy}
                                                className="h-8 text-xs rounded-sm border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="email-sender-summary-btn">
                                            {senderSummary?.busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />} KI: Absender-Historie
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => patchSelected({ done: !selected.done })} className="h-8 text-xs rounded-sm" data-testid="email-done-btn">
                                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {selected.done ? "Als offen markieren" : "Erledigt"}
                                        </Button>
                                        <Button size="sm" variant="outline"
                                                onClick={async () => {
                                                    const toArchive = selected.folder !== "archive";
                                                    await patchSelected({ folder: toArchive ? "archive" : "inbox" });
                                                    setSelected(null);
                                                }}
                                                className="h-8 text-xs rounded-sm" data-testid="email-archive-btn">
                                            <Archive className="w-3.5 h-3.5 mr-1" /> {selected.folder === "archive" ? "Wiederherstellen" : "Archivieren"}
                                        </Button>
                                        <select
                                            value={selected.category || ""}
                                            onChange={(e) => patchSelected({ category: e.target.value || null })}
                                            className="h-8 rounded-sm border border-gray-300 text-xs px-2"
                                            data-testid="email-category-select"
                                        >
                                            <option value="">Kategorie…</option>
                                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        {employees.length > 0 && (
                                            <select
                                                value={selected.assignee_id || ""}
                                                onChange={(e) => {
                                                    const emp = employees.find((x) => x.id === e.target.value);
                                                    patchSelected({ assignee_id: e.target.value || null, assignee_name: emp?.name || null });
                                                }}
                                                className="h-8 rounded-sm border border-gray-300 text-xs px-2"
                                                data-testid="email-assignee-select"
                                            >
                                                <option value="">Zuweisen an…</option>
                                                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                                            </select>
                                        )}
                                        {selected.lead_id ? (
                                            <Link to={`/dealer/leads?lead=${selected.lead_id}`}
                                                className="h-8 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 hover:bg-emerald-100 underline-offset-2 hover:underline"
                                                title="Zum erstellten Lead springen"
                                                data-testid="email-lead-created-badge">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Lead erzeugt — öffnen →
                                                {selected.lead_reaction_min != null
                                                    ? <span className="font-medium">· Reaktion {fmtDur(selected.lead_reaction_min)}{selected.lead_seller_name ? ` (${selected.lead_seller_name})` : ""}</span>
                                                    : (selected.lead_seller_name ? <span className="font-medium text-amber-700">· {selected.lead_seller_name}: noch keine Reaktion</span> : null)}
                                            </Link>
                                        ) : (
                                            <select
                                                value=""
                                                disabled={leadBusy}
                                                onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v !== "") createLeadFromMail(v === "__none__" ? null : v); }}
                                                className="h-8 rounded-sm border border-[#FFCC00] bg-[#FFFBEB] font-bold text-xs px-2 disabled:opacity-60"
                                                title="Aus dieser E-Mail einen Lead erzeugen und optional direkt zuweisen"
                                                data-testid="email-create-lead-select"
                                            >
                                                <option value="">{leadBusy ? "Lead wird erzeugt…" : "⚡ Lead erzeugen…"}</option>
                                                <option value="__none__">Ohne Zuweisung</option>
                                                {employees.map((e) => <option key={e.id} value={e.id}>Zuweisen an {e.name}</option>)}
                                            </select>
                                        )}

                                    </div>
                                </div>
                                <div className="p-4 text-sm max-h-[55vh] overflow-y-auto">
                                    {selected.body_html
                                        ? <iframe title="E-Mail-Inhalt" srcDoc={`<base target="_blank">${selected.body_html}`} sandbox="allow-popups allow-popups-to-escape-sandbox" className="w-full min-h-[40vh] bg-white border-0" data-testid="email-body-iframe" />
                                        : <pre className="whitespace-pre-wrap font-sans text-sm">{linkifyText(selected.body_text)}</pre>}
                                    {(selected.attachments || []).length > 0 && (
                                        <AttachmentList messageId={selected.id} attachments={selected.attachments} />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {connect && <ConnectAccountDialog onClose={() => setConnect(false)} onCreated={loadAccounts} />}
            {senderSummary && !senderSummary.busy && (
                <Dialog open onOpenChange={(o) => !o && setSenderSummary(null)}>
                    <DialogContent className="max-w-2xl w-[95vw]" data-testid="email-sender-summary-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-base">KI-Zusammenfassung — {senderSummary.sender}</DialogTitle>
                        </DialogHeader>
                        <div className="max-h-[60vh] overflow-y-auto">
                            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-testid="email-sender-summary-text">{senderSummary.text || "Keine Inhalte gefunden."}</pre>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm"
                                    onClick={() => { navigator.clipboard?.writeText(senderSummary.text || ""); toast.success("Zusammenfassung kopiert"); }}
                                    data-testid="email-sender-summary-copy">Kopieren</Button>
                            <Button size="sm" className="h-8 text-xs bg-[#111827] text-white rounded-sm" onClick={() => setSenderSummary(null)} data-testid="email-sender-summary-close">Schließen</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
            {rulesOpen && <RulesDialog employees={employees} accountId={active?.id} initial={typeof rulesOpen === "object" ? rulesOpen : null} onClose={() => setRulesOpen(false)} />}
            {sigEdit && <SignatureDialog account={sigEdit} onClose={() => setSigEdit(null)} onSaved={loadAccounts} />}
            {compose && active && <Composer account={active} accounts={accounts} replyTo={compose.replyTo} forwardOf={compose.forwardOf} draft={compose.draft} autoAi={compose.autoAi} onClose={() => setCompose(null)} onSent={loadMessages} />}
        </div>
    );
}

function Plug2() { return <Plus className="w-4 h-4 mr-1" />; }

/* ---------------- Anhänge: Vorschau & Download (Jun-2026, User-Wunsch) ---------------- */
const fmtSize = (n) => {
    if (!n) return "";
    const kb = n / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`;
};

function AttachmentList({ messageId, attachments }) {
    const [preview, setPreview] = useState(null); // {filename, type, url}
    const [busyIdx, setBusyIdx] = useState(-1);
    const [attachFor, setAttachFor] = useState(null); // {idx, filename}

    const fetchBlob = async (idx, download) => {
        const r = await api.get(`/email/messages/${messageId}/attachments/${idx}`,
            { responseType: "blob", params: download ? { download: 1 } : {} });
        return URL.createObjectURL(r.data);
    };

    const openPreview = async (a, idx) => {
        setBusyIdx(idx);
        try {
            const url = await fetchBlob(idx, false);
            const t = (a.content_type || "").toLowerCase();
            if (t.startsWith("image/") || t.includes("pdf")) {
                setPreview({ filename: a.filename, type: t, url });
            } else {
                const el = document.createElement("a");
                el.href = url; el.download = a.filename || "Anhang";
                document.body.appendChild(el); el.click(); el.remove();
                toast.success("Anhang heruntergeladen (keine Vorschau für diesen Dateityp)");
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Anhang konnte nicht geladen werden"); }
        setBusyIdx(-1);
    };

    const downloadAtt = async (a, idx) => {
        setBusyIdx(idx);
        try {
            const url = await fetchBlob(idx, true);
            const el = document.createElement("a");
            el.href = url; el.download = a.filename || "Anhang";
            document.body.appendChild(el); el.click(); el.remove();
        } catch (e) { toast.error(e?.response?.data?.detail || "Download fehlgeschlagen"); }
        setBusyIdx(-1);
    };

    return (
        <div className="mt-3 pt-3 border-t border-gray-100" data-testid="email-attachments">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5 flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Anhänge ({attachments.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
                {attachments.map((a, idx) => (
                    <div key={idx} className="inline-flex items-center gap-1.5 border border-gray-300 bg-gray-50 rounded-sm pl-2 pr-1 py-1"
                        data-testid={`email-attachment-${idx}`}>
                        <FileText className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                        <button type="button" onClick={() => openPreview(a, idx)} disabled={busyIdx === idx}
                            className="text-xs font-bold text-[#111827] hover:underline underline-offset-2 max-w-[220px] truncate disabled:opacity-50"
                            title="Vorschau öffnen" data-testid={`email-attachment-preview-${idx}`}>
                            {a.filename}
                        </button>
                        {a.size ? <span className="text-[10px] text-gray-400">{fmtSize(a.size)}</span> : null}
                        {busyIdx === idx
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                            : (<>
                                <button type="button" onClick={() => openPreview(a, idx)} className="p-1 text-gray-500 hover:text-[#111827]" title="Vorschau" data-testid={`email-attachment-eye-${idx}`}>
                                    <Eye className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={() => downloadAtt(a, idx)} className="p-1 text-gray-500 hover:text-[#111827]" title="Herunterladen" data-testid={`email-attachment-download-${idx}`}>
                                    <Download className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={() => setAttachFor({ idx, filename: a.filename })} className="p-1 text-gray-500 hover:text-emerald-700" title="Am Fahrzeug ablegen (Dispo-Dokumentenarchiv)" data-testid={`email-attachment-to-vehicle-${idx}`}>
                                    <Bike className="w-3.5 h-3.5" />
                                </button>
                            </>)}
                    </div>
                ))}
            </div>
            {attachFor && (
                <AttachToVehicleDialog messageId={messageId} idx={attachFor.idx} filename={attachFor.filename}
                    onClose={() => setAttachFor(null)} />
            )}
            {preview && (
                <Dialog open onOpenChange={(o) => { if (!o) { URL.revokeObjectURL(preview.url); setPreview(null); } }}>
                    <DialogContent className="max-w-4xl w-[95vw]" data-testid="email-attachment-preview-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-base truncate pr-8">{preview.filename}</DialogTitle>
                        </DialogHeader>
                        <div className="max-h-[75vh] overflow-auto bg-gray-100 rounded-sm flex items-center justify-center">
                            {preview.type.startsWith("image/")
                                ? <img src={preview.url} alt={preview.filename} className="max-w-full h-auto" />
                                : <iframe title={preview.filename} src={preview.url} className="w-full h-[70vh] border-0 bg-white" />}
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm"
                                onClick={() => { const el = document.createElement("a"); el.href = preview.url; el.download = preview.filename || "Anhang"; document.body.appendChild(el); el.click(); el.remove(); }}
                                data-testid="email-attachment-dialog-download">
                                <Download className="w-3.5 h-3.5 mr-1" /> Herunterladen
                            </Button>
                            <Button size="sm" className="h-8 text-xs bg-[#111827] text-white rounded-sm"
                                onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}>Schließen</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

/* ------- Anhang am Fahrzeug ablegen (Jun-2026, User-Wunsch) ------- */
const DOC_KINDS = [
    ["", "Automatisch (Dateiname)"], ["zb1", "ZB1 (Fahrzeugbrief)"], ["zb2", "ZB2 (Fahrzeugschein)"],
    ["coc", "COC-Papier"], ["contract", "Kaufvertrag"], ["invoice", "Rechnung"],
    ["handover_protocol", "Übergabeprotokoll"], ["other", "Sonstiges"],
];
const guessKind = (name = "") => {
    const n = name.toLowerCase();
    if (n.includes("zb1") || n.includes("brief")) return "zb1";
    if (n.includes("zb2") || n.includes("schein")) return "zb2";
    if (n.includes("coc")) return "coc";
    if (n.includes("kaufvertrag") || n.includes("vertrag") || n.includes("kv_")) return "contract";
    if (n.includes("rechnung") || n.includes("invoice")) return "invoice";
    if (n.includes("übergabe") || n.includes("uebergabe")) return "handover_protocol";
    return "other";
};

function AttachToVehicleDialog({ messageId, idx, filename, onClose }) {
    const [listings, setListings] = useState([]);
    const [q, setQ] = useState("");
    const [vehicle, setVehicle] = useState(null);
    const [kind, setKind] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/listings/mine?limit=200").then((r) => setListings(r.data?.listings || r.data || [])).catch(() => {});
    }, []);
    const matches = q.trim().length >= 2 && !vehicle
        ? listings.filter((l) => `${l.title || ""} ${l.vin || ""} ${l.internal_number || ""}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
        : [];

    const submit = async () => {
        if (!vehicle || busy) return;
        setBusy(true);
        try {
            const r = await api.post(`/email/messages/${messageId}/attachments/${idx}/attach-to-listing`,
                { listing_id: vehicle.id, kind: kind || guessKind(filename) });
            toast.success(`„${filename}" am Fahrzeug abgelegt: ${r.data.listing_title || vehicle.title}`);
            onClose();
        } catch (e) { toast.error(e?.response?.data?.detail || "Ablage fehlgeschlagen"); }
        setBusy(false);
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="email-attach-vehicle-dialog">
                <DialogHeader><DialogTitle className="text-base">Anhang am Fahrzeug ablegen</DialogTitle></DialogHeader>
                <p className="text-xs text-gray-500 -mt-2">„{filename}" wird im Dokumentenarchiv der Disposition des gewählten Fahrzeugs gespeichert (wie ein Dispo-Upload).</p>
                <div className="relative">
                    {vehicle ? (
                        <div className="flex items-center gap-2 text-xs font-bold bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-2" data-testid="email-attach-vehicle-selected">
                            <Bike className="w-4 h-4 text-emerald-700 shrink-0" />
                            <span className="flex-1 truncate">{vehicle.title}</span>
                            <button type="button" onClick={() => setVehicle(null)} className="text-red-500 hover:text-red-700"><X className="w-3.5 h-3.5" /></button>
                        </div>
                    ) : (
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Fahrzeug suchen (Titel, FIN, interne Nummer)…" className="rounded-sm h-9" data-testid="email-attach-vehicle-search" />
                    )}
                    {matches.length > 0 && (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg max-h-56 overflow-y-auto">
                            {matches.map((l) => (
                                <button key={l.id} type="button" onClick={() => { setVehicle(l); setQ(""); }}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100" data-testid={`email-attach-vehicle-opt-${l.id}`}>
                                    <span className="font-bold">{l.title}</span>{l.vin ? ` · FIN ${l.vin}` : ""}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 rounded-sm border border-gray-300 text-sm px-2 w-full" data-testid="email-attach-kind-select">
                    {DOC_KINDS.map(([k, label]) => <option key={k} value={k}>{label}{k === "" ? ` → ${DOC_KINDS.find(([kk]) => kk === guessKind(filename))?.[1] || "Sonstiges"}` : ""}</option>)}
                </select>
                <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm" onClick={onClose}>Abbrechen</Button>
                    <Button size="sm" onClick={submit} disabled={!vehicle || busy} className="h-8 text-xs bg-[#111827] text-white rounded-sm" data-testid="email-attach-vehicle-submit">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileUp className="w-3.5 h-3.5 mr-1" />} Am Fahrzeug ablegen
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
